import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Building2, ClipboardCheck, BarChart3, Network, Quote } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceCard from "@/components/ServiceCard";
import useSeo from "@/hooks/useSeo";
import { SERVICES, TESTIMONIALS, PHONE_DISPLAY, PHONE_TEL, OFFICE_IMAGE, SERVICE_BY_SLUG } from "@/lib/data";

const UNS = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=75`;

const COMMERCIAL_SLUGS = ["maintenance", "cleaning", "general-repairs", "electrical", "plumbing", "painting", "cctv-antennas-cabling", "locksmithing"];

const COMMERCIAL_FAQS = [
  { q: "Can you handle multi-site programs?", a: "Yes. We coordinate scheduled and reactive maintenance across portfolios of any size — strata, retail, hospitality, office, industrial. One contact across every site." },
  { q: "What does your reporting look like?", a: "Per-job reporting plus monthly summaries: jobs raised, jobs closed, average response time, spend by site, photos before/after. Standard PDF and CSV; happy to integrate with your PM platform." },
  { q: "Do you work to SLAs?", a: "Yes. Standard response SLAs are 24–48 hours for non-urgent and same-day for urgent. We're happy to write to a custom SLA for portfolio agreements." },
  { q: "How does invoicing work?", a: "Per-job or monthly consolidated, payable on net-14 or net-30. We can split by site, cost-centre or owner as needed." },
  { q: "Can you provide a single point of contact?", a: "Always. You get one coordinator who owns every job, every site, every trade. No re-explaining the property to a new person each call." },
];

const SECTORS = [
  { t: "Real estate & strata", b: "Reactive jobs and planned cycles across rentals, owner's corp and managed portfolios.", img: UNS("1486325212027-8081e485255e") },
  { t: "Retail & hospitality", b: "After-hours work, fit-out maintenance, urgent same-day call-outs.", img: UNS("1517457373958-b7bdd4587205") },
  { t: "Office & commercial", b: "End-of-lease make-goods, fit-out adjustments, planned cleaning and repairs.", img: UNS("1497366216548-37526070297c") },
  { t: "Industrial & specialised", b: "Warehousing, light industrial, regulated environments with full compliance docs.", img: UNS("1581094288338-2314dddb7ece") },
];

export default function Commercial() {
  useSeo({
    title: "For Property Managers & Business — RSR Australia",
    description: "Multi-trade maintenance across strata, retail, hospitality, office and commercial sites. SLAs, monthly reporting, single point of contact.",
  });
  const commercialServices = SERVICES.filter(s => COMMERCIAL_SLUGS.includes(s.slug));
  const commercialTestimonials = TESTIMONIALS.filter(t => /facilities|manager|strata|retail/i.test(t.role));

  return (
    <>
      {/* Hero — split with photo block */}
      <section data-testid="commercial-hero" className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule-accent" />
              <span className="overline">For property managers & business</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] tracking-tighter leading-[0.96] font-extrabold">
              Multi-trade maintenance, <span className="text-accent">across every site.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground">
              Reactive callouts, planned programs and full fit-outs for property managers, strata, retail, hospitality and commercial operators across Australia. One coordinator, every trade, every site.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact?service=Commercial%20program" data-testid="commercial-hero-quote" className="group inline-flex items-center gap-3 bg-accent text-white px-7 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                Request a proposal <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="commercial-hero-call" className="inline-flex items-center gap-3 border border-foreground/25 hover:border-foreground px-7 py-4 font-semibold text-sm transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[{ k: "14", v: "Trades" }, { k: "<24h", v: "Avg response" }, { k: "Net-30", v: "Standard terms" }].map((s, i) => (
                <div key={i} className="border-l border-accent pl-4">
                  <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter leading-none">{s.k}</div>
                  <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -right-3 left-6 bottom-10 border border-accent/40 hidden sm:block" />
              <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
                <img src={OFFICE_IMAGE} alt="A modern commercial office hallway" className="w-full h-full object-cover duotone" />
                <div className="absolute inset-0 bg-gradient-to-tr from-foreground/10 to-transparent" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-accent text-white p-5 max-w-[260px] hidden sm:block">
                <div className="text-[10px] font-bold tracking-[0.28em] uppercase opacity-80">Reporting</div>
                <div className="mt-2 font-display text-lg font-bold leading-tight">Numbers your team can pass upstream.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors with images */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">Who we work with</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Built for the way <br /> portfolios actually run.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">We work across the categories that keep property managers and facilities leads up at night — and we report in a way you can actually pass upstream.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {SECTORS.map((s, i) => (
              <div key={i} data-testid={`commercial-sector-${i}`} className="relative group bg-card border-r border-b border-border overflow-hidden flex flex-col">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt={s.t} className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                    <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-white/95">0{i + 1}</span>
                  </div>
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <h3 className="font-display text-xl font-bold tracking-tight">{s.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed image band */}
      <section className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
        <img src={OFFICE_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover duotone" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/70 to-foreground/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12">
            <div className="max-w-2xl text-white">
              <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>One inbox · Every site</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-extrabold">
                Same coordinator <br /> across your <span className="text-accent">whole portfolio.</span>
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* What you get with image */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="overline">Why portfolios choose RSR</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              One inbox. <br /> Every job. <br /> Every site.
            </h2>
            <p className="mt-6 text-base text-muted-foreground max-w-md">
              The same coordinator covers your whole portfolio. The same reporting format. The same SLA. The same workmanship standard.
            </p>
            <div className="mt-8 relative aspect-[4/3] overflow-hidden border border-border bg-secondary">
              <img src={SERVICE_BY_SLUG.maintenance.image} alt="Tradesperson on a commercial site" className="w-full h-full object-cover duotone" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7 space-y-px">
            {[
              { icon: Network, t: "Single point of contact across all sites", b: "One coordinator who knows your portfolio. No re-explaining a building to a new person each call." },
              { icon: ClipboardCheck, t: "Written SLAs & response times", b: "24–48h non-urgent, same-day urgent. Custom SLAs available on portfolio agreements." },
              { icon: BarChart3, t: "Monthly reporting that travels upstream", b: "Jobs raised, jobs closed, spend by site, average response, photos before/after. PDF + CSV exports." },
              { icon: Building2, t: "Scaled crews, vetted trades", b: "Every trade licensed and insured. We scale the crew up or down to match the schedule, not the other way round." },
            ].map((b, i) => (
              <div key={i} data-testid={`commercial-benefit-${i}`} className="flex items-start gap-5 bg-background border border-border p-6">
                <b.icon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight">{b.t}</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{b.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services commercial use */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="overline">Most-used commercial services</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">What managers book most.</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold nav-link inline-flex items-center gap-2">All 14 services <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {commercialServices.map(s => <ServiceCard key={s.slug} service={s} testIdPrefix="commercial-service" />)}
          </div>
        </div>
      </section>

      {/* Big quote pull */}
      {commercialTestimonials[0] && (
        <section className="py-20 sm:py-24 border-t border-border bg-foreground text-background relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
            <img src={OFFICE_IMAGE} alt="" className="w-full h-full object-cover duotone opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-foreground/70 to-foreground" />
          </div>
          <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-8">
              <Quote className="w-10 h-10 text-accent" strokeWidth={1.5} />
              <blockquote className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tighter text-white">
                "{commercialTestimonials[0].quote}"
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-white font-display font-bold text-lg">
                  {commercialTestimonials[0].name.charAt(0)}
                </div>
                <div>
                  <div className="text-base font-semibold">{commercialTestimonials[0].name}</div>
                  <div className="text-xs text-background/60">{commercialTestimonials[0].role}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Commercial FAQ</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">For property managers.</h2>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {COMMERCIAL_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-display text-lg sm:text-xl font-semibold tracking-tight hover:no-underline hover:text-accent py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6 max-w-2xl">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden bg-foreground text-background">
            <img src={OFFICE_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/30" />
            <div className="relative p-8 lg:p-14 grid grid-cols-12 gap-8 items-center">
              <div className="col-span-12 lg:col-span-8">
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Portfolio proposal</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold leading-[1.05]">
                  Get a tailored maintenance plan.
                </h3>
                <p className="mt-3 text-sm text-background/75 max-w-xl">Send us your site list and current pain points. We'll come back with a proposal — SLAs, reporting cadence, indicative rates and onboarding plan.</p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
                <Link to="/contact?service=Commercial%20program" data-testid="commercial-cta-quote" className="inline-flex items-center justify-between gap-3 bg-accent text-white px-6 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                  Request a proposal <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${PHONE_TEL}`} data-testid="commercial-cta-call" className="inline-flex items-center justify-between gap-3 border border-white/30 hover:border-white px-6 py-4 font-semibold text-sm text-white transition-colors">
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
